import React, { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import ChatWidget from './ChatWidget';
import EmailVerificationBanner from './EmailVerificationBanner';
import TopBar from './navigation/TopBar';
import { buildTopBarNav } from './navigation/platformNav';
import { useNavStyle } from './navigation/navStyle';
import { ChatNavContext, SettingsShell, useChatSidebarChrome } from './navigation/SidebarNav';

/*
 * The signed-in app shell, in one of two navigations each person switches between in the account menu
 * (Rudy 2026-11-03; `navigation/navStyle`):
 * - 'sidebar' (ChatGPT style, design "Option A", the default): the chat is the app and its list column carries the
 *   brand, the bell and the account (ChatNavContext); every other page opens in Settings (`SettingsShell`).
 * - 'top' (design "Option B"): one bar across the top (`navigation/TopBar`).
 * Both read the same menu model (`navigation/platformNav`, `buildTopBarNav`), so they reach the same pages.
 * What is decided here: auth's sign-out, the verification banner, the chat widget, and the page frame:
 * every page sits in one centred column of a readable width with even padding (Rudy 2026-11-03: pages
 * were "widened af"); pages draw no outer padding or width of their own. The chat fills the window.
 */

const PAGE_FRAME = 'mx-auto w-full px-4 pb-12 pt-8 sm:px-6 lg:px-8';

export function isChatPath(pathname = '') {
  return pathname === '/ai-chat' || pathname.startsWith('/ai-chat/');
}

export function pageFrameClass(pathname = '') {
  if (isChatPath(pathname)) return '';
  // The admin console's tables (users, models, tenants) need more room than a page of cards and forms.
  return `${PAGE_FRAME} ${pathname.startsWith('/admin') ? 'max-w-7xl' : 'max-w-6xl'}`;
}

export default function Layout() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const [navStyle] = useNavStyle();
  const nav = useMemo(() => buildTopBarNav({ user, hostname }), [user, hostname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  const chatChrome = useChatSidebarChrome({ nav, user, onLogout: handleLogout });

  if (navStyle === 'sidebar') {
    if (!isChatPath(pathname)) {
      return (
        <>
          <SettingsShell nav={nav} user={user} onLogout={handleLogout} frameClass={pageFrameClass(pathname)} />
          <ChatWidget />
        </>
      );
    }
    return (
      <ChatNavContext.Provider value={chatChrome}>
        <div className="flex min-h-screen flex-col bg-gray-950">
          <main className="flex-1">
            <EmailVerificationBanner />
            <Outlet />
          </main>
          <ChatWidget />
        </div>
      </ChatNavContext.Provider>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-950">
      <TopBar user={user} hostname={hostname} onLogout={handleLogout} />

      <main className="flex-1">
        <EmailVerificationBanner />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={pageFrameClass(pathname)}
        >
          <Outlet />
        </motion.div>
      </main>

      {/* Chat Widget */}
      <ChatWidget />
    </div>
  );
}
